import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CacheMap } from '@app/classes';
import { Country, Language, PhonePrefix, State } from '@app/models';
import { AuthService, DictionaryService, SettingsService, ToastService } from '@app/services';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { CustomerService } from 'app/core/services/customer.service';
import { IosDialogService } from 'app/core/services/ios-dialog.service';
import { ActivationCodeData, FormUtils, SignupData } from 'app/core/utils';
import { ConfirmDialogComponent } from 'app/shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import { FinalizeSignupDialogComponent } from 'app/shared/components/dialogs/finalize-signup-dialog/finalize-signup-dialog.component';
import { MessageType, REGEX_EMAIL, REGEX_SPECIAL_CHARS, REGEX_STRONG_PASSWORD } from 'app/shared/constants';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { combineLatest, lastValueFrom } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss'],
  providers: [ReCaptchaV3Service],
})
export class SignUpComponent implements OnInit, OnDestroy {
  termsAndConditionsAccepted: boolean = false;
  privacyPolicyAccepted: boolean = false;
  isSignupFormSubmitted: boolean = false;
  isLoading: boolean = true;
  isSaving: boolean = false;
  isVatNumberRequired: boolean = false;

  licenseContractDisabled = true;
  privacyPolicyDisabled = true;

  languages!: CacheMap<Language>;
  countries!: CacheMap<Country>;
  states!: CacheMap<State>;
  countryStates: State[] = [];
  phonePrefixes!: CacheMap<PhonePrefix>;

  signupForm: FormGroup = new FormGroup({});
  signupFormErr = {
    country: '',
    state: '',
    language: '',
    city: '',
    zipCode: '',
    address: '',
    firstName: '',
    lastName: '',
    organization: '',
    vatNumber: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  };
  signupFormVal = {
    country: {
      required: 'ERR.FIELD_REQUIRED',
    },
    state: {
      required: 'ERR.FIELD_REQUIRED',
    },
    language: {
      required: 'ERR.FIELD_REQUIRED',
    },
    city: {
      required: 'ERR.FIELD_REQUIRED',
      maxlength: 'ERR.MAX_LENGHT',
    },
    zipCode: {
      required: 'ERR.FIELD_REQUIRED',
      maxlength: 'ERR.MAX_LENGHT',
    },
    address: {
      required: 'ERR.FIELD_REQUIRED',
      maxlength: 'ERR.MAX_LENGHT',
    },
    firstName: {
      required: 'ERR.FIELD_REQUIRED',
      maxlength: 'ERR.MAX_LENGHT',
      pattern: 'ERR.BAD_CHARS',
    },
    lastName: {
      required: 'ERR.FIELD_REQUIRED',
      maxlength: 'ERR.MAX_LENGHT',
      pattern: 'ERR.BAD_CHARS',
    },
    organization: {
      required: 'ERR.FIELD_REQUIRED',
      maxlength: 'ERR.MAX_LENGHT',
    },
    vatNumber: {
      required: 'ERR.FIELD_REQUIRED',
      maxlength: 'ERR.MAX_LENGHT',
    },
    email: {
      required: 'ERR.FIELD_REQUIRED',
      pattern: 'ERR.EMAIL_NOT_VALID',
      maxlength: 'ERR.MAX_LENGHT',
    },
    phone: {
      required: 'ERR.FIELD_REQUIRED',
      maxlength: 'ERR.MAX_LENGHT',
    },
    password: {
      required: 'ERR.FIELD_REQUIRED',
      pattern: 'ERR.WEAK_PASSWORD',
      __mismatch: 'ERR.PASSWORD_MISMATCH',
    },
    confirmPassword: {
      required: 'ERR.FIELD_REQUIRED',
      pattern: 'ERR.WEAK_PASSWORD',
      __mismatch: 'ERR.PASSWORD_MISMATCH',
    },
  };

  constructor(
    private authService: AuthService,
    private fb: FormBuilder,
    private dictionaryService: DictionaryService,
    private customerService: CustomerService,
    private dialog: IosDialogService,
    private translate: TranslateService,
    private settingsService: SettingsService,
    private recaptchaV3Service: ReCaptchaV3Service,
    private toastService: ToastService,
    private router: Router,
  ) {
    this.buildForm();
  }

  ngOnInit(): void {
    const doc = window.document.getElementsByClassName('grecaptcha-badge').item(0);
    if (doc) {
      (doc as HTMLElement).style.visibility = 'visible';
    }

    this.signupForm.valueChanges.pipe(untilDestroyed(this)).subscribe((_) => {
      FormUtils.checkForm(this.signupFormErr, this.signupFormVal, this.signupForm, { skipPristine: true, trim: false });
    });

    combineLatest([this.signupForm.get('password')!.valueChanges, this.signupForm.get('confirmPassword')!.valueChanges])
      .pipe(untilDestroyed(this))
      .subscribe(([password, confirmPassword]) => {
        if (this.isSignupFormSubmitted) {
          this.checkPwdMismatch(password, confirmPassword);
          FormUtils.checkForm(this.signupFormErr, this.signupFormVal, this.signupForm, { skipPristine: true, trim: false });
        }
      });

    this.initData();
  }

  ngOnDestroy(): void {
    const doc = window.document.getElementsByClassName('grecaptcha-badge').item(0);
    if (doc) {
      (doc as HTMLElement).style.visibility = 'hidden';
    }
  }

  goBack() {
    return this.router.navigate(['/home']);
  }

  buildForm(): void {
    this.signupForm = this.fb.group({
      country: ['', Validators.required],
      state: [''],
      language: ['', Validators.required],
      city: ['', [Validators.required, Validators.maxLength(255)]],
      zipCode: ['', [Validators.required, Validators.maxLength(255)]],
      address: ['', [Validators.required, Validators.maxLength(255)]],
      firstName: ['', [Validators.required, Validators.maxLength(100), Validators.pattern(REGEX_SPECIAL_CHARS)]],
      lastName: ['', [Validators.required, Validators.maxLength(100), Validators.pattern(REGEX_SPECIAL_CHARS)]],
      organization: ['', [Validators.required, Validators.maxLength(255)]],
      vatNumber: [''],
      email: ['', [Validators.required, Validators.pattern(REGEX_EMAIL), Validators.maxLength(255)]],
      phone: [{ phoneNumber: null, phonePrefixId: null }, [Validators.required, Validators.maxLength(20)]],
      password: ['', [Validators.required, Validators.pattern(REGEX_STRONG_PASSWORD)]],
      confirmPassword: ['', [Validators.required, Validators.pattern(REGEX_STRONG_PASSWORD)]],
    });
  }

  /**
   * get the basic data
   */
  private async initData(): Promise<void> {
    try {
      const [languages, countries, phonePrefixes, states] = await Promise.all([
        this.dictionaryService.getLanguages(),
        this.dictionaryService.getCountries(),
        this.dictionaryService.getPhonePrefixes(),
        this.dictionaryService.getStates(),
      ]);
      this.languages = languages;
      this.countries = countries;
      this.phonePrefixes = phonePrefixes;
      this.states = states;
      this.subscribeToCountryChanges(this.countries.findByProp('countryCode', 'IT').countryId);
    } catch (e) {
      console.error(e);
      this.toastService.showErrorToast(e);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Subscribe to country form changes: if the country is Italy then the vat number is required
   */
  private subscribeToCountryChanges(italianCountryId: string): void {
    this.signupForm
      .get('country')!
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((selectedCountry) => {
        if (selectedCountry.countryId == italianCountryId) {
          this.isVatNumberRequired = true;
          this.signupForm.get('vatNumber')!.addValidators(Validators.required);
        } else {
          this.isVatNumberRequired = false;
          this.signupForm.get('vatNumber')!.removeValidators(Validators.required);
          this.signupForm.get('vatNumber')!.setErrors(null);
          this.signupFormErr['vatNumber'] = '';
        }

        if (selectedCountry) {
          this.countryStates = [...this.states.filter((state) => state.countryId === selectedCountry.countryId).values()];
          if (!this.countryStates.length) {
            this.signupForm.get('state')!.setValue(null);
            this.signupForm.get('state')!.removeValidators(Validators.required);
          } else {
            this.signupForm.get('state')!.addValidators(Validators.required);
          }
        }
      });
  }

  onLanguageChange(event: Language) {
    this.settingsService.loadLanguage(event).subscribe((_) => {
      this.countries = this.countries.sort((a, b) => {
        const aValue = this.translate.instant(`DB.COUNTRIES.CODE.${a.countryCode}`);
        const bValue = this.translate.instant(`DB.COUNTRIES.CODE.${b.countryCode}`);
        return aValue > bValue ? 1 : -1;
      });
    });
  }

  /**
   * Check if passwords are equal
   *
   * @param password
   * @param confirmPassword
   * @returns
   */
  checkPwdMismatch(password?: string, confirmPassword?: string): boolean {
    if (!password || !confirmPassword) {
      return false;
    }

    if (password != confirmPassword) {
      this.signupForm.controls['password'].setErrors({ __mismatch: true });
      this.signupForm.controls['confirmPassword'].setErrors({ __mismatch: true });
      this.signupFormErr['password'] = this.signupFormVal.password.__mismatch;
      this.signupFormErr['confirmPassword'] = this.signupFormVal.confirmPassword.__mismatch;
      return false;
    }

    this.signupForm.controls['confirmPassword'].setErrors(null);
    this.signupForm.controls['password'].setErrors(null);
    this.signupFormErr['password'] = '';
    this.signupFormErr['confirmPassword'] = '';
    return true;
  }

  /**
   * Send the activation code to customer's email
   */
  async sendAndVerifyActivationCode(): Promise<ActivationCodeData | undefined> {
    const formValue = this.signupForm.value;
    const codeId = await this.customerService.sendEmailActivationCode(
      formValue.firstName,
      formValue.lastName,
      formValue.language.languageId,
      formValue.email,
    );

    return this.dialog.openAndWaitResponse(FinalizeSignupDialogComponent, {
      width: 'min(450px, 90vw)',
      header: this.translate.instant('PAGES.SIGNUP.CODE_SENT_TITLE'),
      data: {
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        languageId: formValue.language.languageId,
        email: formValue.email,
        activationCodeId: codeId,
      },
    });
  }

  /**
   * Signup the customer
   */
  async doSignup(): Promise<void> {
    this.isSignupFormSubmitted = true;
    const formValue = this.signupForm.value;
    const arePasswordsValid = this.checkPwdMismatch(formValue.password, formValue.confirmPassword);
    const valid = FormUtils.checkForm(this.signupFormErr, this.signupFormVal, this.signupForm);

    if (!valid || !arePasswordsValid) {
      return;
    }

    this.isSaving = true;

    try {
      const activationCode: ActivationCodeData | undefined = await this.sendAndVerifyActivationCode();
      if (!activationCode) {
        return;
      }

      const token = await lastValueFrom(this.recaptchaV3Service.execute('SIGNUP'));
      const signupData: SignupData = {
        tokenRecaptcha: token,
        emailActivationCodeId: activationCode.activationCodeId,
        activationCode: activationCode.code,
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        address: formValue.address,
        city: formValue.city,
        zipCode: formValue.zipCode,
        countryId: formValue.country.countryId,
        languageId: formValue.language.languageId,
        phonePrefixId: formValue.phone.phonePrefixId,
        phoneNumber: formValue.phone.phoneNumber,
        vatNumber: formValue.vatNumber,
        organization: formValue.organization,
        email: formValue.email,
        password: formValue.password,
        stateId: formValue.state ?? null,
      };

      const response = await this.customerService.signup(signupData);
      let message = this.translate.instant('PAGES.SIGNUP.COMPLETED_MESSAGE') + '. ';
      if (response.userAlreadyPresentInImAzure) {
        message += `<br /> ${this.translate.instant('PAGES.SIGNUP.USER_ALREADY_REGISTERED_MESSAGE')}`;
      }
      this.dialog
        .openAndWaitResponse(ConfirmDialogComponent, {
          showHeader: false,
          width: 'min(450px, 90vw)',
          closable: false,
          data: {
            title: this.translate.instant('PAGES.SIGNUP.COMPLETED_TITLE'),
            message,
            primaryButtonText: this.translate.instant('PAGES.SIGNUP.GO_TO_LOGIN'),
            showCloseButton: false,
            showCancelButton: false,
            isHTML: true,
          },
        })
        .then(() => this.redirectToLogin());
    } catch (e) {
      this.toastService.showErrorToast(e);
    } finally {
      this.isSaving = false;
    }
  }

  async redirectToLogin() {
    try {
      const { url } = await this.authService.redirectSSO();
      window.open(url, '_self');
    } catch (error) {
      console.error(error);
      this.toastService.showToast(MessageType.ERROR);
    }
  }

  scrollEnded(value: string) {
    switch (value) {
      case 'LICENSE_CONTRACT':
        this.licenseContractDisabled = false;
        break;

      case 'PRIVACY_POLICY':
        this.privacyPolicyDisabled = false;
        break;

      default:
        break;
    }
  }
}
